import styled from '@emotion/styled'
import { Link, Typography } from '@mui/material'
import { styled as muiStyled } from '@mui/material/styles'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { busDriverSignIn, BusDriverSignInResponse } from '../actions/bus-driver'
import Button from '../components/button/Button'
import LoadingIndicator from '../components/LoadingIndicator'
import Page from '../components/page/Page'
import ProgressBar from '../components/ProgressBar'
import config from '../config'
import {
  isNativeApp,
  NFC_SCAN_TIMEOUT_MS,
  NfcScanResult,
  openNfcSettings,
  requestNfcAvailability,
  requestNfcScan,
} from '../lib/nfcBridge'
import { color } from '../Theme'

const Container = muiStyled('div')(({ theme }) => ({
  ...theme.layout.fluidWidth(theme, '500px'),
  padding: theme.spacing('large'),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
}))

const Title = muiStyled('h1')(({ theme }) => ({
  ...theme.typography.h1,
  color: theme.palette.text.primary,
  textAlign: 'center',
  margin: 0,
}))

type StatusMessageVariant = 'info' | 'success' | 'error'

const StatusMessage = muiStyled('div', {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: StatusMessageVariant }>(({ theme, variant }) => {
  const backgroundColor = {
    info: theme.palette.primary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
  }[variant]

  return {
    ...theme.typography.body1,
    padding: theme.spacing('normal'),
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
    borderRadius: '8px',
    background: backgroundColor,
    color: theme.palette.common.white,
  }
})

const OutlineButton = styled(Button)`
  background: transparent;
  color: ${(p) => color(p.color, 'primaryBlue')};
  outline: 2px solid currentColor;
  outline-offset: -2px;
`

const getNativePackageName = () => {
  switch (config.environment) {
    case 'test':
      return 'fi.vr.kenttanative.staging'
    case 'dev':
      if (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost') {
        return 'fi.vr.kenttanative.local'
      }
      return 'fi.vr.kenttanative.dev'
    case 'prod':
    default:
      return 'fi.vr.kenttanative'
  }
}

const getNativeIntentUrl = () => {
  const isLocalhost =
    window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost'
  const scheme = window.location.protocol.replace(':', '')
  const host = isLocalhost ? '10.0.2.2:3000' : window.location.host
  const fallbackUrl = `${window.location.origin}/check-in`
  const packageName = getNativePackageName()

  return `intent://${host}/bus-check-in#Intent;scheme=${scheme};package=${packageName};S.browser_fallback_url=${encodeURIComponent(
    fallbackUrl
  )};end`
}

const getSignInTime = () => {
  const signInDate = new Date()

  return {
    hours: signInDate.getHours().toString().padStart(2, '0'),
    minutes: signInDate.getMinutes().toString().padStart(2, '0'),
  }
}

type CheckInState =
  | { status: 'idle' }
  | { status: 'checking-nfc' }
  | { status: 'scanning' }
  | { status: 'signing-in' }
  | { status: 'success'; result: BusDriverSignInResponse }
  | { status: 'repeated' }
  | { status: 'cancelled' }
  | { status: 'nfc-disabled' }
  | { status: 'error'; message: string }

const BusDriverCheckInPage = () => {
  const { t } = useTranslation()
  const [state, setState] = useState<CheckInState>(
    isNativeApp() ? { status: 'checking-nfc' } : { status: 'idle' }
  )
  const scanAbortController = useRef<AbortController | null>(null)

  useEffect(() => {
    let mounted = true

    const applyAvailability = (availability: {
      supported: boolean | null
      enabled: boolean | null
    }) => {
      if (availability.supported === false) {
        setState({ status: 'error', message: t('busDriverSignIn.nfcNotSupported') })
        return
      }

      if (availability.enabled === false) {
        setState({ status: 'nfc-disabled' })
        return
      }

      setState((currentState) =>
        currentState.status === 'checking-nfc' || currentState.status === 'nfc-disabled'
          ? { status: 'idle' }
          : currentState
      )
    }

    const handleNativeMessage = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (detail?.type === 'nfc-availability-changed') {
        applyAvailability(detail.availability)
      }
    }

    if (isNativeApp()) {
      window.addEventListener('kentta-native-message', handleNativeMessage)

      requestNfcAvailability().then((availability) => {
        if (!mounted) {
          return
        }

        applyAvailability(availability)
      })
    }

    return () => {
      mounted = false
      window.removeEventListener('kentta-native-message', handleNativeMessage)
      scanAbortController.current?.abort()
    }
  }, [t])

  if (!isNativeApp()) {
    return (
      <Page menu="start">
        <Container>
          <Title>{t('busDriverSignIn.signInToShift')}</Title>
          <StatusMessage variant="info">{t('busDriverSignIn.useNativeApp')}</StatusMessage>
          <Link
            variant="body1"
            fontWeight="bold"
            color="common.kenttaBlue"
            href={getNativeIntentUrl()}
          >
            {t('busDriverSignIn.openApp')}
          </Link>
        </Container>
      </Page>
    )
  }

  const handleCheckIn = async () => {
    if (
      scanAbortController.current ||
      state.status === 'scanning' ||
      state.status === 'signing-in'
    ) {
      return
    }

    const abortController = new AbortController()

    scanAbortController.current = abortController
    setState({ status: 'scanning' })

    const scanResult: NfcScanResult = await requestNfcScan(abortController.signal)

    if (scanAbortController.current === abortController) {
      scanAbortController.current = null
    }

    if (scanResult.type === 'nfc-not-supported') {
      setState({ status: 'error', message: t('busDriverSignIn.nfcNotSupported') })
      return
    }

    if (scanResult.type === 'nfc-disabled') {
      setState({ status: 'nfc-disabled' })
      return
    }

    if (scanResult.type === 'nfc-scan-cancelled') {
      setState({ status: 'cancelled' })
      return
    }

    if (scanResult.type === 'nfc-scan-error') {
      setState({
        status: 'error',
        message:
          scanResult.error === 'timeout' ? t('busDriverSignIn.scanTimeout') : scanResult.error,
      })
      return
    }

    setState({ status: 'signing-in' })

    try {
      const response = await busDriverSignIn(scanResult.data.tagId, new Date().toISOString())

      if (response.result === 'REPEATED_NOTIFICATION') {
        setState({ status: 'repeated' })
      } else if (response.result === 'SUCCESS') {
        setState({ status: 'success', result: response })
      } else {
        setState({ status: 'error', message: response.result })
      }
    } catch {
      setState({ status: 'error', message: t('busDriverSignIn.scanError') })
    }
  }

  const handleCancelScan = () => {
    scanAbortController.current?.abort()
  }

  const handleOpenNfcSettings = () => {
    openNfcSettings()
  }

  const loading = state.status === 'scanning' || state.status === 'signing-in'
  const signInTime = state.status === 'success' ? getSignInTime() : null
  const showScanButton =
    state.status !== 'checking-nfc' && state.status !== 'nfc-disabled' && !loading

  return (
    <Page menu="start">
      <Container>
        <Title>{t('busDriverSignIn.signInToShift')}</Title>

        {showScanButton && <Button onClick={handleCheckIn}>{t('busDriverSignIn.scanNfc')}</Button>}

        {state.status === 'checking-nfc' && <LoadingIndicator size="normal" padded={false} />}

        {state.status === 'scanning' && (
          <>
            <LoadingIndicator size="normal" padded={false} />
            <StatusMessage variant="info">{t('busDriverSignIn.scanning')}</StatusMessage>
            <ProgressBar
              timeoutMs={NFC_SCAN_TIMEOUT_MS}
              labelKey="busDriverSignIn.scanTimeoutRemaining"
            />
            <OutlineButton onClick={handleCancelScan}>{t('action.cancel')}</OutlineButton>
          </>
        )}

        {state.status === 'signing-in' && <LoadingIndicator size="normal" padded={false} />}

        {state.status === 'success' && (
          <>
            <StatusMessage variant="success">{t('busDriverSignIn.scanSuccess')}</StatusMessage>
            {signInTime && (
              <Typography variant="body1" fontWeight="bold" color="text.primary" textAlign="center">
                {t('busDriverSignIn.signInTime', signInTime)}
              </Typography>
            )}
          </>
        )}

        {state.status === 'repeated' && (
          <StatusMessage variant="info">{t('busDriverSignIn.alreadySignedIn')}</StatusMessage>
        )}

        {state.status === 'cancelled' && (
          <StatusMessage variant="info">{t('busDriverSignIn.scanCancelled')}</StatusMessage>
        )}

        {state.status === 'nfc-disabled' && (
          <>
            <StatusMessage variant="error">{t('busDriverSignIn.nfcDisabled')}</StatusMessage>
            <Button onClick={handleOpenNfcSettings}>{t('busDriverSignIn.enableNfc')}</Button>
          </>
        )}

        {state.status === 'error' && (
          <StatusMessage variant="error">
            {t('busDriverSignIn.scanError')}: {state.message}
          </StatusMessage>
        )}
      </Container>
    </Page>
  )
}

BusDriverCheckInPage.displayName = 'BusDriverCheckInPage'

export default BusDriverCheckInPage
